import { createEntityAdapter } from '@reduxjs/toolkit';
import { AiChatResponseStatus } from '@payer-admin-ui/api/src/ai/responses/AiChatResponse';
import type {
  AiChatMessageContent,
  AiChatMessageRole,
} from '@payer-admin-ui/api/src/ai/responses/AiChatConversationResponse';
import type { AiChatModelMode } from '@payer-admin-ui/api/src/ai/requests/AiChatRequest';
import type { PayerErrorLike } from '@payer-admin-ui/components-v2/src/error/PayerErrorMessage';
import { MerchantAdminState } from '../../redux/store';

export type AiChatMessage = {
  id: string;
  role: AiChatMessageRole;
  content: AiChatMessageContent;
  createdAt: string;
  requestId: string;
};

export type AiChatAttachment = {
  id: string;
  originalFilename: string | null;
  contentType: string | null;
  sizeBytes: number;
  creationTime: string | null;
};

export type AiConversation = {
  id: string;
  isEphemeral: boolean;
  creationTime: string | null;
  modificationTime: string | null;
  title: string | null;
  isLoadingHistory: boolean;
  isFetching: boolean;
  error: unknown | null;
  attachmentError: PayerErrorLike | null;
  isUploadingAttachments: boolean;
  deletingAttachmentIds: string[];
  attachments: AiChatAttachment[];
  attachmentIds: string[];
  messages: AiChatMessage[];
  lastUserMessage: string | null;
  lastModelMode: AiChatModelMode | null;
  lastResponseStatus: AiChatResponseStatus | null;
  lastResponseStatusInfo: string | null;
  hasLoadedMessages: boolean;
};

export const entityAdapter = createEntityAdapter<AiConversation>({
  selectId: conversation => conversation.id,
  sortComparer: (a, b) =>
    (new Date(b.modificationTime as any).getTime() || 0) - (new Date(a.modificationTime as any).getTime() || 0),
});

export const initialState = entityAdapter.getInitialState({
  activeConversationId: null as string | null,
  openConversationIds: [] as string[],
});

export type AiChatState = typeof initialState;

export const EMPTY_MESSAGES: AiChatMessage[] = [];

export const getOrCreateConversation = (state: AiChatState, conversationId: string): AiConversation => {
  const existing = state.entities[conversationId];
  if (existing) return existing;

  entityAdapter.addOne(state, {
    id: conversationId,
    isEphemeral: false,
    creationTime: null,
    modificationTime: null,
    title: null,
    isLoadingHistory: false,
    isFetching: false,
    error: null,
    attachmentError: null,
    isUploadingAttachments: false,
    deletingAttachmentIds: [],
    attachments: [],
    attachmentIds: [],
    messages: [],
    lastUserMessage: null,
    lastModelMode: null,
    lastResponseStatus: null,
    lastResponseStatusInfo: null,
    hasLoadedMessages: false,
  });

  return state.entities[conversationId] as AiConversation;
};

export const aiChatSelectors = entityAdapter.getSelectors<MerchantAdminState>(state => state.ai.chat);

export const selectActiveAiChatConversationMessages = (state: MerchantAdminState): AiChatMessage[] => {
  const conversationId = state.ai.chat.activeConversationId;
  if (!conversationId) return EMPTY_MESSAGES;
  return state.ai.chat.entities[conversationId]?.messages ?? EMPTY_MESSAGES;
};
